import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Building2, Award, CheckCircle, Globe, Mail, Package, Calendar, ExternalLink } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Manufacturer() {
  const [searchParams] = useSearchParams();
  const manufacturerId = searchParams.get('id');

  const brand = searchParams.get('brand');

  const { data: manufacturer } = useQuery({
    queryKey: ['manufacturer', manufacturerId, brand],
    queryFn: async () => {
      if (manufacturerId) {
        const result = await base44.entities.Manufacturer.filter({ id: manufacturerId });
        return result[0];
      } else if (brand) {
        const result = await base44.entities.Manufacturer.filter({ brand: brand });
        return result[0] || { name: brand, brand: brand };
      }
    },
    enabled: !!manufacturerId || !!brand,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['manufacturer-products', manufacturer?.brand],
    queryFn: () => base44.entities.Product.filter({ brand: manufacturer?.brand }),
    enabled: !!manufacturer?.brand,
  });

  const { data: news = [] } = useQuery({
    queryKey: ['manufacturer-news', manufacturerId],
    queryFn: () => base44.entities.ManufacturerNews.filter({ manufacturer_id: manufacturerId, published: true }),
    enabled: !!manufacturerId,
  });

  if (!manufacturer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-20">
      {/* Cover Image */}
      <div className="relative h-48 bg-gradient-to-br from-blue-500 to-indigo-600">
        {manufacturer.cover_image_url && (
          <img 
            src={manufacturer.cover_image_url} 
            alt={manufacturer.name}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      <main className="max-w-4xl mx-auto px-4 -mt-16 relative z-10">
        {/* Header Card */}
        <Card className="p-6 mb-6">
          <div className="flex items-start gap-4">
            {manufacturer.logo_url ? (
              <img 
                src={manufacturer.logo_url} 
                alt={manufacturer.name}
                className="w-20 h-20 rounded-xl object-cover border-2 border-white shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Building2 className="w-10 h-10 text-white" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">{manufacturer.name}</h1>
                {manufacturer.verified && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Verificado
                  </div>
                )}
              </div>
              {manufacturer.brand && (
                <p className="text-gray-500 mb-3">Marca: {manufacturer.brand}</p>
              )}
              {manufacturer.description && (
                <p className="text-gray-600">{manufacturer.description}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-4">
                {manufacturer.website && (
                  <a
                    href={manufacturer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Globe className="w-4 h-4" />
                    Sitio web
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {manufacturer.contact_email && (
                  <a
                    href={`mailto:${manufacturer.contact_email}`}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700"
                  >
                    <Mail className="w-4 h-4" />
                    Contacto
                  </a>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {/* History */}
          {manufacturer.history && (
            <Card className="p-6 md:col-span-2">
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Nuestra Historia
              </h2>
              <p className="text-gray-600 leading-relaxed">{manufacturer.history}</p>
            </Card>
          )}

          {/* Certifications */}
          {manufacturer.certifications && manufacturer.certifications.length > 0 && (
            <Card className="p-6">
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Certificaciones
              </h2>
              <div className="space-y-2">
                {manufacturer.certifications.map((cert, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{cert}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Quality Policy */}
        {manufacturer.quality_policy && (
          <Card className="p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Política de Calidad</h2>
            <p className="text-gray-600 leading-relaxed">{manufacturer.quality_policy}</p>
          </Card>
        )}

        {/* News */}
        {news.length > 0 && (
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Comunicados y Noticias</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {news.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="overflow-hidden hover:shadow-md transition-shadow">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-3 mb-3">{item.content}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(item.created_date), "d 'de' MMMM, yyyy", { locale: es })}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Productos ({products.length})
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card key={product.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex gap-3">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-100" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                    {product.scaneat_score !== undefined && (
                      <div className={`
                        inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-2
                        ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                          product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'}
                      `}>
                        Score: {product.scaneat_score}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}